/* Draft growth experience: explicit choices, shipping clarity, safe interaction events. */
(() => {
  // ANALYTICS HOOKS (loop #39). The kill switch is the first statement of the inline block above
  // {{ content_for_header }} in layout/theme.liquid: window.JSOC_ANALYTICS_HOOKS_OFF = true disables every
  // hook #39 added -- the fabric map (written by that inline block), the automation flag, story_read,
  // scroll_depth, page_engagement, batched list impressions and the GA identifiers on the cart -- and
  // restores the previous one-event-per-card impressions.
  const hooksOn = window.JSOC_ANALYTICS_HOOKS_OFF !== true;
  // Release marker: an execution witness a probe can read (Shopify minifies assets, so served bytes cannot be hashed).
  try { document.documentElement.dataset.jGrowthRelease = 'r1-consent-2026-09-12'; } catch (_) {}
  // The automation flag must exist before ANY jsoc: event on this page, including replayed navigation
  // events below: the GA4 pixel reads it to stamp traffic_type=internal.
  if (hooksOn) {
    try { if (navigator.webdriver === true) sessionStorage.setItem('jsoc_automation', '1'); } catch (_) {}
  }
  const path = location.pathname;
  const lastSegment = () => path.split('/').filter(Boolean).pop() || '';
  const pageType = path.includes('/products/') ? 'product'
    // A story needs a handle: the /blogs/field-notes index (with or without a trailing slash) is 'other' (AC-5).
    : /^\/blogs\/field-notes\/[^/]+/.test(path) ? 'story'
    : path === '/' ? 'home'
    : path.startsWith('/collections') ? 'collection'
    : path === '/search' ? 'search'
    : path === '/cart' ? 'cart'
    : path.startsWith('/pages/') ? 'page' : 'other';
  const publish = (name, data = {}) => {
    try {
      const result = window.Shopify?.analytics?.publish(`jsoc:${name}`, data);
      result?.catch?.(() => {});
    } catch (_) { /* Analytics must never interrupt shopping. */ }
  };
  const queueNavigation = (name, data) => {
    try {
      const pending = JSON.parse(sessionStorage.getItem('jsoc_navigation_events') || '[]');
      pending.push({ name, data, at: Date.now() });
      sessionStorage.setItem('jsoc_navigation_events', JSON.stringify(pending.slice(-6)));
    } catch (_) { publish(name, data); }
  };
  // Route-changing clicks are dispatched from the destination page so Shopify's
  // sandbox is not destroyed before Google transmits the interaction.
  try {
    const pending = JSON.parse(sessionStorage.getItem('jsoc_navigation_events') || '[]');
    sessionStorage.removeItem('jsoc_navigation_events');
    pending.filter((e) => Date.now() - e.at < 60000).forEach((e) => publish(e.name, e.data));
  } catch (_) {}
  if (location.pathname === '/cart') {
    try {
      const addition = JSON.parse(sessionStorage.getItem('jsoc_cart_add_confirmed') || 'null');
      const cart = JSON.parse(document.getElementById('j-cart-analytics')?.textContent || 'null');
      sessionStorage.removeItem('jsoc_cart_add_confirmed');
      const line = cart?.items.find((i) => String(i.variant_id) === addition?.variant_id);
      if (line && Date.now() - addition.at < 60000 && addition.quantity > 0 && line.quantity >= addition.quantity) {
        const confirmed = { variant_id: addition.variant_id, quantity: addition.quantity,
          price: line.final_price / 100, currency: cart.currency };
        if (line.product_id) confirmed.product_id = String(line.product_id);
        if (line.sku) confirmed.sku = String(line.sku);
        publish('cart_add_confirmed', confirmed);
      }
    } catch (_) {}
  }
  const listName = (node) => node.closest('[data-j-list]')?.dataset.jList ||
    (node.closest('.j-core') ? 'home_core' : node.closest('.j-collection') ? 'collection' : 'related');
  // Product-level item for list events. `index` is the card's 1-based position within its own list.
  const listOf = (card) => card.closest('[data-j-list]') || document.body;
  const cardItem = (card) => ({
    item_id: String(card.dataset.jProduct || ''),
    item_name: card.dataset.jName || '',
    price: Number(card.dataset.jPrice),
    index: [...listOf(card).querySelectorAll('[data-j-item]')].indexOf(card) + 1,
    sku: card.dataset.jItem,
  });
  document.addEventListener('click', (event) => {
    const anchor = event.target.closest('a');
    const card = anchor?.closest('[data-j-item]');
    const track = anchor && anchor.origin === location.origin && !anchor.target && !event.metaKey && !event.ctrlKey && anchor.pathname !== location.pathname ? queueNavigation : publish;
    // Top-level item_id stays the SKU so the GA4 pixel v17 keeps accepting the event.
    if (card) track('select_item', { item_id: card.dataset.jItem, item_list_id: listName(card), items: [cardItem(card)] });
    if (anchor && location.pathname.startsWith('/blogs/field-notes/') && anchor.pathname.startsWith('/products/')) {
      track('story_product_click', { story_id: location.pathname.split('/').pop(), product_handle: anchor.pathname.split('/').pop() });
    }
  });
  document.addEventListener('toggle', (event) => {
    if (event.target.matches?.('details.jsoc-size[open]')) {
      publish('size_guide_open', { product_handle: location.pathname.split('/').pop() });
    }
  }, true);
  document.addEventListener('change', (event) => {
    const picker = event.target.closest('variant-selects');
    if (!picker) return;
    const selected = [...picker.querySelectorAll('fieldset')].map((f) => f.querySelector(':checked')?.value);
    const store = picker.closest('section')?.querySelector('[data-j-variants]');
    if (!store) return;
    const variants = JSON.parse(store.textContent || '[]');
    const variant = variants.find((v) => v.options.every((o, i) => o === selected[i]));
    const sizeGroup = picker.querySelector('[data-j-option="size"]');
    const sizeChosen = !sizeGroup || !!sizeGroup.querySelector('input:checked');
    if (variant && sizeChosen) publish('variant_selected', { variant_id: String(variant.id), product_handle: location.pathname.split('/').pop() });
  });

  // Only a successful server response may produce a signup event. The marker is per attempt,
  // and expires so a later page load cannot turn a failed attempt into a conversion.
  document.addEventListener('submit', (event) => {
    const form = event.target.closest('[data-j-newsletter]');
    if (!form) return;
    try { sessionStorage.setItem('jsoc_signup_attempt', JSON.stringify({ placement: form.dataset.jNewsletter, at: Date.now() })); } catch (_) {}
  });
  document.addEventListener('submit', (event) => {
    const form = event.target.closest('[data-j-contact]');
    if (!form) return;
    const selected = form.querySelector('[data-j-contact-topic]')?.value;
    const topic = ['order', 'delivery', 'product-issue', 'review', 'fit', 'community', 'correction', 'other'].includes(selected) ? selected : 'other';
    try { sessionStorage.setItem('jsoc_contact_attempt', JSON.stringify({ topic, at: Date.now() })); } catch (_) {}
  });
  if (document.querySelector('[data-j-contact-success]')) {
    try {
      const attempt = JSON.parse(sessionStorage.getItem('jsoc_contact_attempt') || 'null');
      sessionStorage.removeItem('jsoc_contact_attempt');
      if (attempt && Date.now() - attempt.at >= 0 && Date.now() - attempt.at < 15 * 60 * 1000) {
        // A community submission is reported once, as contact_success with topic "community" (#39 AC-11).
        publish('contact_success', { placement: 'contact', topic: attempt.topic });
      }
    } catch (_) {}
  }
  const success = document.querySelector('[data-j-signup-success]');
  if (success) {
    try {
      const attempt = JSON.parse(sessionStorage.getItem('jsoc_signup_attempt') || 'null');
      if (attempt && Date.now() - attempt.at < 15 * 60 * 1000) {
        publish('sign_up', { method: 'field_notes', placement: attempt.placement });
        sessionStorage.removeItem('jsoc_signup_attempt');
      }
    } catch (_) {}
  }
  if (location.pathname.startsWith('/blogs/field-notes/')) {
    publish('story_view', { story_id: location.pathname.split('/').pop() });
  }

  const seen = new WeakSet();
  if ('IntersectionObserver' in window && !hooksOn) {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(({ target, isIntersecting }) => {
        if (!isIntersecting || seen.has(target)) return;
        seen.add(target); observer.unobserve(target);
        publish('view_item_list', { item_id: target.dataset.jItem, item_list_id: listName(target) });
      });
    }, { threshold: 0.5 });
    document.querySelectorAll('[data-j-item]').forEach((card) => observer.observe(card));
  } else if ('IntersectionObserver' in window) {
    // Batched impressions: one event per list, 1 s after the first unreported card of the batch becomes
    // >=50% visible, carrying every unreported card visible at flush (max 20). Each card is reported once.
    const BATCH_DELAY_MS = 1000;
    const BATCH_MAX = 20;
    const visible = new Set();
    const timers = new Map();
    const flush = (list) => {
      timers.delete(list);
      const ready = [...list.querySelectorAll('[data-j-item]')]
        .filter((card) => visible.has(card) && !seen.has(card)).slice(0, BATCH_MAX);
      if (!ready.length) return;
      ready.forEach((card) => seen.add(card));
      const items = ready.map(cardItem);
      publish('view_item_list', { item_list_id: listName(ready[0]), items, item_id: items[0].sku });
      const more = [...list.querySelectorAll('[data-j-item]')].some((card) => visible.has(card) && !seen.has(card));
      if (more) timers.set(list, setTimeout(() => flush(list), BATCH_DELAY_MS));
    };
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(({ target, isIntersecting }) => {
        if (!isIntersecting) { visible.delete(target); return; }
        if (seen.has(target)) return;
        visible.add(target);
        const list = listOf(target);
        if (!timers.has(list)) timers.set(list, setTimeout(() => flush(list), BATCH_DELAY_MS));
      });
    }, { threshold: 0.5 });
    document.querySelectorAll('[data-j-item]').forEach((card) => observer.observe(card));
  }

  if (hooksOn && pageType === 'story') {
    // One visible exposure for the controlled Guts-to-Try CTA, never a scroll-event count.
    const storyCta = document.querySelector('[data-j-story-product-exposure]');
    if (storyCta && 'IntersectionObserver' in window) {
      let exposed = false;
      const observeVisible = () => {
        if (!exposed && document.visibilityState === 'visible') {
          exposureObserver.unobserve(storyCta);
          exposureObserver.observe(storyCta);
        }
      };
      const exposureObserver = new IntersectionObserver((entries) => {
        if (exposed || document.visibilityState !== 'visible') return;
        if (!entries.some((entry) => entry.target === storyCta && entry.isIntersecting && entry.intersectionRatio >= 0.5)) return;
        exposed = true;
        publish('story_product_exposure', {
          story_id: storyCta.dataset.storyId,
          product_handle: storyCta.dataset.productHandle,
        });
        exposureObserver.disconnect();
        document.removeEventListener('visibilitychange', observeVisible);
      }, { threshold: 0.5 });
      document.addEventListener('visibilitychange', observeVisible);
      observeVisible();
    }

    // story_read: once per page load, when the end-of-article sentinel is >=75% visible.
    const sentinel = document.querySelector('[data-j-story-end]');
    if (sentinel && 'IntersectionObserver' in window) {
      const readObserver = new IntersectionObserver((entries) => {
        if (!entries.some((e) => e.isIntersecting && e.intersectionRatio >= 0.75)) return;
        readObserver.disconnect();
        publish('story_read', { story_id: lastSegment() });
      }, { threshold: 0.75 });
      readObserver.observe(sentinel);
    }
  }

  if (hooksOn && pageType === 'product') {
    // scroll_depth (product pages only; stories are measured by story_read): 50 and 90, each at most once per
    // page load, measured at the viewport's bottom edge.
    const reached = new Set();
    let depthPending = false;
    const measureDepth = () => {
      depthPending = false;
      const height = document.documentElement.scrollHeight;
      if (!height) return;
      const ratio = (window.scrollY + window.innerHeight) / height;
      [50, 90].forEach((percent) => {
        if (ratio * 100 < percent || reached.has(percent)) return;
        reached.add(percent);
        publish('scroll_depth', { percent, page_type: 'product', product_handle: lastSegment() });
      });
    };
    window.addEventListener('scroll', () => {
      if (!depthPending) { depthPending = true; requestAnimationFrame(measureDepth); }
    }, { passive: true });
    measureDepth();
  }

  if (hooksOn) {
    // page_engagement: whole seconds the page was visible AND focused since the last publish, sent once
    // per hidden transition (visibilitychange->hidden or pagehide, whichever comes first), capped at 1800.
    let engagedMs = 0;
    let activeSince = null;
    let hiddenSent = false;
    const isActive = () => document.visibilityState === 'visible' && document.hasFocus();
    const tick = () => {
      const now = performance.now();
      if (activeSince !== null) engagedMs += now - activeSince;
      activeSince = isActive() ? now : null;
    };
    const sendEngagement = () => {
      tick();
      if (hiddenSent) return;
      hiddenSent = true;
      const seconds = Math.min(1800, Math.floor(engagedMs / 1000));
      engagedMs = 0;
      if (seconds > 0) publish('page_engagement', { engaged_seconds: seconds, page_type: pageType });
    };
    window.addEventListener('focus', tick);
    window.addEventListener('blur', tick);
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') sendEngagement();
      else { hiddenSent = false; tick(); }
    });
    setInterval(() => { if (isActive()) { hiddenSent = false; sendEngagement(); hiddenSent = false; } }, 10000);
    window.addEventListener('pagehide', sendEngagement);
    window.addEventListener('pageshow', () => { hiddenSent = false; tick(); });
    tick();
  }

  if (hooksOn) {
    // GA IDENTIFIERS ON THE CART (#39 AC-16). The order must carry the browser's GA client id so a server-side
    // purchase (#41) de-duplicates against the browser's in GA4. Consent, not the cookie, decides: a _ga
    // cookie survives consent withdrawal, so identifiers are written only while Shopify's customer privacy API
    // reports analytics AND sale-of-data consent, and removed otherwise. The cart state comes from the
    // server-rendered #j-cart-ga block, so no /cart.js request is needed to decide.
    const CONSENT_TIMEOUT_MS = 5000;
    let rendered = null;
    try { rendered = JSON.parse(document.getElementById('j-cart-ga')?.textContent || 'null'); } catch (_) { rendered = null; }
    const state = rendered && typeof rendered.count === 'number'
      ? { count: rendered.count, cid: rendered.cid || null, sid: rendered.sid || null } : null;
    const readCookies = () => {
      const ga = []; let session = null;
      document.cookie.split(';').forEach((part) => {
        const i = part.indexOf('='); if (i < 0) return;
        // Only the two GA cookies are ever decoded, each in its own try: decoding EVERY cookie let one malformed
        // cookie elsewhere (promo=100%) throw out of this block and abort the sticky buy bar below it.
        const name = part.slice(0, i).trim();
        if (name !== '_ga' && name !== '_ga_K06YN9QPLC') return;
        let value = null;
        try { value = decodeURIComponent(part.slice(i + 1).trim()); } catch (_) { value = null; }
        // A _ga is counted by NAME even when it cannot be decoded: a second _ga is identity ambiguity (AC-16).
        if (name === '_ga') ga.push(value);
        else session = value;
      });
      const fields = ga.length === 1 && ga[0] !== null ? ga[0].split('.') : [];
      const cid = fields.length >= 2 ? fields.slice(-2).join('.') : null;
      const sidMatch = session ? /(?:^|\.)s(\d+)/.exec(session) : null;
      return { gaCount: ga.length, cid: cid && /^\d+\.\d+$/.test(cid) ? cid : null, sid: sidMatch ? sidMatch[1] : null };
    };
    let consentApi = null; let consentSettled = false;
    const loadConsent = (then) => {
      const timer = setTimeout(() => { consentSettled = true; then(); }, CONSENT_TIMEOUT_MS);
      const done = (err) => {
        clearTimeout(timer);
        consentApi = !err && window.Shopify?.customerPrivacy ? window.Shopify.customerPrivacy : null;
        consentSettled = true;
        then();   // also runs when the callback arrives after the timeout: a late callback re-evaluates (C10)
      };
      try {
        if (window.Shopify?.customerPrivacy) return done(null);
        if (typeof window.Shopify?.loadFeatures !== 'function') return done(new Error('no loadFeatures'));
        window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], (error) => done(error));
      } catch (e) { done(e); }
    };
    const consentAllowed = () => {
      try { return window.JSOC_ANALYTICS_EXCLUDED !== true && !!consentApi && consentApi.analyticsProcessingAllowed() === true && consentApi.saleOfDataAllowed() === true; }
      catch (_) { return false; }
    };
    // Identifier requests are tracked so that what the page REMEMBERS is never mistaken for what the server HOLDS:
    //  * pendingWrites counts identifier writes not yet settled. A withdrawal sends a removal at once whenever the
    //    cart holds identifiers OR a write is pending -- remembered state alone was empty while a write was in flight,
    //    so the removal used to be skipped and the write landed on an opted-out cart.
    //  * dirty means the remembered state cannot be trusted: requests overlapped (response ORDER does not show which
    //    one the server applied last), or a request failed or lost its response (it may have been applied anyway).
    //    While dirty, the desired state is re-sent even when it matches memory -- including a removal after retries
    //    were exhausted -- and dirty clears only on a successful response to a request that overlapped nothing (that
    //    response IS the server's state).
    //  * when the last request settles, the state is re-evaluated.
    //  * automatic re-evaluations are capped per external trigger; exhaustion is marked on <html data-j-ga-sync>.
    // Residual, stated: a keepalive write that lands after the page is gone cannot be cleaned up by this page. The
    // next page load renders the cart's identifiers server-side and removes them if consent is still withheld.
    const MAX_AUTO = 3;
    let outstanding = 0; let pendingWrites = 0; let dirty = false; let autoLeft = MAX_AUTO; let firstSeen = null;
    const remember = (cart) => {
      if (cart && state) {
        state.count = cart.item_count;
        state.cid = cart.attributes?._ga_client_id || null;
        state.sid = cart.attributes?._ga_session_id || null;
      }
    };
    const post = (attributes, alone) => fetch('/cart/update.js', {
      method: 'POST', keepalive: true, credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ attributes }),
    }).then((r) => { if (!r.ok) throw new Error(`update ${r.status}`); return r.json(); })
      .then((cart) => { if (alone && outstanding === 1) { remember(cart); dirty = false; } else { dirty = true; } })
      .catch(() => { dirty = true; });
    const settle = (wasWrite) => {
      outstanding -= 1;
      if (wasWrite) pendingWrites -= 1;
      if (outstanding > 0) return;
      if (autoLeft <= 0) { try { document.documentElement.dataset.jGaSync = 'exhausted'; } catch (_) {} return; }
      autoLeft -= 1;
      runSync();
    };
    const syncGaCartNow = () => {
      if (!state || !consentSettled) return;
      const cookies = readCookies();
      if (firstSeen === null) firstSeen = cookies;
      const allowed = consentAllowed() && cookies.gaCount === 1 && cookies.cid !== null;
      const want = allowed && state.count > 0 ? { cid: cookies.cid, sid: cookies.sid } : { cid: null, sid: null };
      const removing = want.cid === null;
      if (!dirty && pendingWrites === 0 && want.cid === state.cid && want.sid === state.sid) return;
      // nothing to remove: the cart holds no identifiers, no write is pending, and memory is trustworthy
      if (removing && !dirty && pendingWrites === 0 && state.cid === null && state.sid === null) return;
      const attributes = {
        _ga_client_id: want.cid === null ? '' : want.cid,
        _ga_session_id: want.sid === null ? '' : want.sid,
      };
      if (outstanding > 0 && !removing) return;   // a write waits; settle() re-evaluates
      // A removal is never held behind a write: it goes out now, with keepalive, so an unload cannot drop it.
      const alone = outstanding === 0;
      if (!alone) dirty = true;
      outstanding += 1;
      if (!removing) pendingWrites += 1;
      post(attributes, alone).then(() => settle(!removing));
    };
    // 2. Containment: nothing in the identifier sync may throw into the code that follows this block.
    const runSync = () => { try { syncGaCartNow(); } catch (_) { /* analytics must never interrupt shopping */ } };
    const syncGaCart = () => { autoLeft = MAX_AUTO; runSync(); };
    loadConsent(syncGaCart);
    document.addEventListener('visitorConsentCollected', () => { consentApi = window.Shopify?.customerPrivacy || consentApi; consentSettled = true; syncGaCart(); });
    setTimeout(() => {
      try {
        const now = readCookies();
        if (!firstSeen || firstSeen.cid === null || now.cid !== firstSeen.cid || now.sid !== firstSeen.sid) syncGaCart();
        // One more bounded look if gtag has still not written _ga at +3 s (C6).
        if (now.cid === null) setTimeout(syncGaCart, 7000);
      } catch (_) { /* analytics must never interrupt shopping */ }
    }, 3000);
  }

  // The sticky control delegates to the real product form and mirrors its pending/sold-out state.
  const productForm = document.querySelector('product-form[data-j-section]');
  if (productForm) {
    const original = productForm.querySelector('[name="add"]');
    const price = document.getElementById(`price-${productForm.dataset.jSection}`);
    const getPicker = () => document.getElementById(`variant-selects-${productForm.dataset.jSection}`);
    const bar = document.createElement('div'); bar.className = 'j-sticky-buy'; bar.hidden = true;
    const priceLabel = document.createElement('span');
    const button = document.createElement('button'); button.type = 'button'; button.className = 'j-btn';
    bar.append(priceLabel, button); document.body.append(bar);
    const sync = () => {
      const picker = getPicker();
      const missing = picker?.querySelector('[data-j-option="size"]') && !picker.querySelector('[data-j-option="size"] :checked');
      button.textContent = missing ? 'Choose your size' : original.querySelector('span')?.textContent.trim() || 'Add to cart';
      button.disabled = original.disabled || original.getAttribute('aria-disabled') === 'true';
      const actual = price?.querySelector('.price-item--regular');
      priceLabel.textContent = actual?.textContent.trim() || '';
      const box = original.getBoundingClientRect();
      const obstructed = document.querySelector('modal-dialog[open], product-modal[open], cart-drawer.active, .shopify-pc__banner__dialog, .shopify-pc__prefs__dialog, .j-mobile-nav:not([hidden])');
      bar.hidden = window.innerWidth > 749 || obstructed || (box.top >= 0 && box.bottom <= window.innerHeight);
    };
    button.addEventListener('click', () => {
      const picker = getPicker();
      const group = picker?.querySelector('[data-j-option="size"]');
      if (group && !group.querySelector(':checked')) {
        group.scrollIntoView({ block: 'center', behavior: 'smooth' });
        group.querySelector('input')?.focus({ preventScroll: true });
      } else original.click();
    });
    let pending = false;
    const schedule = () => { if (!pending) { pending = true; requestAnimationFrame(() => { pending = false; sync(); }); } };
    new MutationObserver(schedule).observe(productForm, { subtree: true, attributes: true, childList: true, characterData: true });
    if (price) new MutationObserver(schedule).observe(price, { subtree: true, childList: true, characterData: true });
    window.addEventListener('scroll', schedule, { passive: true }); window.addEventListener('resize', schedule);
    document.addEventListener('click', schedule); document.addEventListener('change', schedule); sync();
  }

  let cartVersion = 0;
  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, () => {
      cartVersion += 1;
      document.querySelectorAll('[data-j-shipping-result]').forEach((n) => { n.textContent = 'Your cart changed. Estimate shipping again.'; });
    });
  }
  document.addEventListener('submit', async (event) => {
    const form = event.target.closest('[data-j-shipping-form]');
    if (!form) return;
    event.preventDefault();
    const button = form.querySelector('button'); if (button.disabled) return;
    const output = form.querySelector('[data-j-shipping-result]');
    const version = cartVersion; const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 20000);
    button.disabled = true; output.textContent = 'Calculating shipping…';
    const params = new URLSearchParams({ 'shipping_address[country]': 'United States', 'shipping_address[province]': form.elements.province.value, 'shipping_address[zip]': form.elements.zip.value });
    const root = window.Shopify?.routes?.root || '/';
    const request = async (path, method = 'GET') => {
      const response = await fetch(`${root}cart/${path}?${params}`, { method, signal: controller.signal, headers: { Accept: 'application/json' } });
      if (!response.ok) throw new Error('rate_unavailable');
      return response.json();
    };
    try {
      await request('prepare_shipping_rates.json', 'POST');
      let rates = null;
      for (let attempt = 0; attempt < 10 && !rates; attempt += 1) {
        if (attempt) await new Promise((resolve) => setTimeout(resolve, 1200));
        rates = await request('async_shipping_rates.json');
      }
      if (cartVersion !== version || !form.isConnected) return;
      if (!rates) throw new Error('rate_timeout');
      output.replaceChildren();
      const items = rates.shipping_rates || [];
      if (!items.length) output.textContent = 'No estimate is available for this destination. Check the address at checkout or contact the shop.';
      items.forEach((rate) => {
        const line = document.createElement('p');
        const currency = rate.currency || form.closest('[data-j-currency]').dataset.jCurrency || 'USD';
        line.textContent = `${rate.presentment_name || rate.name}: ${new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(Number(rate.price))}`;
        output.append(line);
      });
    } catch (_) {
      if (cartVersion === version && form.isConnected) output.textContent = 'We couldn’t calculate shipping. Check your state and ZIP, or continue to checkout for the final options.';
    } finally { clearTimeout(timeout); button.disabled = false; }
  });
})();

(() => {
 const topic = document.querySelector('[data-j-contact-topic]');
 if (!topic) return;
 const params = new URLSearchParams(location.search);
 const allowed = Array.from(topic.options).map(o => o.value);
 if (allowed.includes(params.get('topic'))) topic.value = params.get('topic');
 const product = document.getElementById('ContactForm-product');
 if (product && /^[a-z0-9-]{1,150}$/.test(params.get('product') || '')) product.value = params.get('product');
 const update = () => {
  const fit = document.querySelector('[data-j-fit-help]');
  const review = document.querySelector('[data-j-review-help]');
  const body = document.getElementById('ContactForm-body');
  if (fit) fit.hidden = topic.value !== 'fit';
  if (review) review.hidden = topic.value !== 'review';
  const activeHelp = topic.value === 'fit' ? fit : topic.value === 'review' ? review : null;
  if (body && activeHelp) body.setAttribute('aria-describedby', activeHelp.id);
  else if (body) body.removeAttribute('aria-describedby');
 };
 topic.addEventListener('change', update); update();
})();
